import { useEffect, useState } from 'react';

import { fetchKnowledgeNoteCard } from '@/api/knowledge/api';

/** Minimal metadata fetched for each linked card ID to render the pill label and badge. */
export interface LinkedCardMeta {
  cardId: string;
  title: string;
  sourceType: 'note' | 'document' | 'file_url' | 'markdown_import' | undefined;
  sourceUrl: string | null | undefined;
}

export interface UseLinkedDocumentsResult {
  cards: LinkedCardMeta[];
}

/**
 * Fetches minimal metadata for a list of knowledge card IDs so they can be
 * rendered as labelled pill tags. Falls back to a placeholder on fetch failure.
 */
export function useLinkedDocuments(
  cardIds: string[],
  accessToken: string,
  clientId: string,
  projectId: string,
): UseLinkedDocumentsResult {
  const [cards, setCards] = useState<LinkedCardMeta[]>([]);

  useEffect(() => {
    if (cardIds.length === 0) {
      setCards([]);
      return;
    }
    void Promise.all(
      cardIds.map(async (id) => {
        try {
          const result = await fetchKnowledgeNoteCard(accessToken, clientId, id, projectId);
          if (result.found && result.card) {
            return {
              cardId: id,
              title: result.card.title,
              sourceType: result.card.source_type,
              sourceUrl: result.card.source_url,
            } satisfies LinkedCardMeta;
          }
          // Card exists but wasn't found (still processing) — show a placeholder pill
          return { cardId: id, title: 'Document', sourceType: undefined, sourceUrl: undefined };
        } catch {
          // Network/auth error — still show a placeholder so the user can open it
          return { cardId: id, title: 'Document', sourceType: undefined, sourceUrl: undefined };
        }
      }),
    ).then((results) => {
      setCards(results);
    });
  }, [cardIds, accessToken, clientId, projectId]);

  return { cards };
}
